<script lang="ts">
    import { afterUpdate, beforeUpdate, onMount, tick } from "svelte";
    import { get, readable } from "svelte/store";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import type {
        ChatConversation,
        ChatMessage,
        ChatRoom,
        ChatThreadSummary,
        ChatTimelineItem,
    } from "../../Connection/ChatConnection";
    import getCloseImg from "../../images/get-close.png";
    import { selectedChatMessageToReply, shouldRestoreChatStateStore } from "../../Stores/ChatStore";
    import { intentionallyClosedChatDuringMeetingStore } from "../../../Stores/ChatStore";
    import { selectedRoomStore } from "../../Stores/SelectRoomStore";
    import { hideActionBarStoreBecauseOfChatBar } from "../../ChatSidebarWidthStore";
    import {
        roomSidePanelStore,
        roomTimelineFocusStore,
        type RoomTimelineFocusRequest,
    } from "../../Stores/RoomSidePanelStore";
    import { matrixSecurity } from "../../Connection/Matrix/MatrixSecurity";
    import { localUserStore } from "../../../Connection/LocalUserStore";
    import { ProximityChatRoom } from "../../Connection/Proximity/ProximityChatRoom";
    import LL from "../../../../i18n/i18n-svelte";
    import Message from "./Message.svelte";
    import MessageInputBar from "./MessageInputBar.svelte";
    import MessageSystem from "./MessageSystem.svelte";
    import PollCard from "./PollCard.svelte";
    import TypingUsers from "./TypingUsers.svelte";
    import { shouldReserveFloatingCloseButtonSpace } from "./RoomTimelineHeaderLayout";
    import { IconChevronLeft, IconChevronRight, IconLoader, IconMailBox, IconInfoCircle } from "@wa-icons";

    export let room: ChatConversation;
    export let backAction: (() => void) | undefined = undefined;
    export let backButtonTestId = "chatBackward";
    export let timelineTestId = "roomTimeline";
    export let showRoomSidePanelToggle = false;

    const chatConnection = gameManager.chatConnection;
    const shouldRetrySendingEvents = chatConnection.shouldRetrySendingEvents;
    let myChatID = localUserStore.getChatId();

    // Time gap threshold for message grouping (5 minutes)
    const TIME_GAP_THRESHOLD = 5 * 60 * 1000;

    let messageListRef: HTMLDivElement;
    let autoScroll = true;
    let onScrollTop = false;
    let oldScrollHeight = 0;
    let loadingMessagePromise: Promise<void> | undefined = undefined;
    let scrollTimer: ReturnType<typeof setTimeout>;
    let shouldDisplayLoader = false;
    let messageInputBarRef: MessageInputBar;
    let lastTimelineFocusSequence = 0;
    let initialMessagesLoaded = false;

    const gameScene = gameManager.getCurrentGameScene();
    const chatRoomsEnableInAdmin = gameScene.room.isChatEnabled;
    const direction = document.documentElement.getAttribute("dir") || "ltr";

    function isChatRoom(conversation: ChatConversation): conversation is ChatRoom {
        return conversation.conversationKind === "room";
    }

    const emptyThreadSummaries = readable<readonly ChatThreadSummary[]>([]);

    $: roomSidePanelToggleIsOpen = $roomSidePanelStore.isOpen ?? false;
    $: roomName = room?.name;
    $: typingMembers = room.typingMembers;
    $: timelineItems = room.timelineItems;
    $: initializationState = room.initializationState;
    $: initializationError = room.initializationError;
    // threads exist only on ChatRoom, not when this timeline shows a thread (ThreadPanel).
    $: threadsStore = isChatRoom(room) ? room.threads : undefined;
    $: threadSummariesStore = threadsStore ?? emptyThreadSummaries;
    $: unreadThreadCount = $threadSummariesStore.filter((thread) => thread.hasUnreadMessages).length;
    $: shouldReserveHeaderEndSpace = shouldReserveFloatingCloseButtonSpace(
        $hideActionBarStoreBecauseOfChatBar,
        showRoomSidePanelToggle,
        roomSidePanelToggleIsOpen
    );
    $: if (initialMessagesLoaded && $roomTimelineFocusStore) {
        focusTimelineEvent($roomTimelineFocusStore).catch((error) => console.error(error));
    }

    type RenderItem =
        | { kind: "separator"; key: string; label: string }
        | { kind: "item"; key: string; timelineItem: ChatTimelineItem; showHeader: boolean };

    function isValidDate(d: Date) {
        return d instanceof Date && !Number.isNaN(d.getTime());
    }

    function getTimelineItemDate(timelineItem: ChatTimelineItem): Date | undefined {
        if (timelineItem?.date && isValidDate(timelineItem.date)) {
            return timelineItem.date;
        }
        return undefined;
    }

    function isSameLocalDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    function dayLabel(d: Date): string {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (isSameLocalDay(d, today)) return $LL.chat.timeLine.today();
        if (isSameLocalDay(d, yesterday)) return $LL.chat.timeLine.yesterday();

        return new Intl.DateTimeFormat(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
        }).format(d);
    }

    function dayKey(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
    }

    $: renderItems = (() => {
        const out: RenderItem[] = [];
        let lastDayKey: string | null = null;
        let previousMessage: ChatMessage | undefined = undefined;
        const currentTimelineItems = $timelineItems ?? [];

        for (const timelineItem of currentTimelineItems) {
            const d = getTimelineItemDate(timelineItem);
            let insertedSeparator = false;

            if (d && isValidDate(d)) {
                const k = dayKey(d);
                if (k !== lastDayKey) {
                    out.push({
                        kind: "separator",
                        key: `sep-${k}`,
                        label: dayLabel(d),
                    });
                    lastDayKey = k;
                    insertedSeparator = true;
                }
            }

            if (insertedSeparator) {
                previousMessage = undefined;
            }

            const currentMessage = timelineItem.kind === "message" ? timelineItem.message : undefined;
            const previousMessageUserId = previousMessage?.sender?.spaceUserId ?? previousMessage?.sender?.chatId;
            const currentMessageUserId = currentMessage?.sender?.spaceUserId ?? currentMessage?.sender?.chatId;
            const timeDiff =
                currentMessage?.date && previousMessage?.date
                    ? currentMessage.date.getTime() - previousMessage.date.getTime()
                    : Infinity;
            const isRepeatedSender =
                !!currentMessage &&
                !!previousMessageUserId &&
                previousMessageUserId === currentMessageUserId &&
                timeDiff < TIME_GAP_THRESHOLD;

            out.push({
                kind: "item",
                key: `${timelineItem.kind}-${timelineItem.id}`,
                timelineItem,
                showHeader: !isRepeatedSender,
            });
            previousMessage = currentMessage;
        }

        return out;
    })();

    onMount(() => {
        initMessages()
            .catch((error) => console.error(error))
            .finally(() => focusPendingTimelineRequestOrScrollToBottom().catch((error) => console.error(error)));
    });

    async function initMessages() {
        if (!messageListRef) return;
        initialMessagesLoaded = false;

        const loadMessages = async () => {
            try {
                await room.ensureTimelineInitialized();
                if (get(room.isEncrypted) && get(matrixSecurity.isEncryptionRequiredAndNotSet)) {
                    return;
                }

                await room.loadMorePreviousMessages();

                if (get(room.hasPreviousMessage) && isViewportNotFilled()) {
                    await loadMessages();
                }
            } catch (error) {
                console.error(`Failed to load messages: ${error}`);
            }
        };

        try {
            await loadMessages();
            setFirstListItem();
            initialMessagesLoaded = true;
        } catch (error) {
            console.error(`Failed to load messages: ${error}`);
        }
    }

    function retryInitialization() {
        room.ensureTimelineInitialized().catch((error) => console.error(error));
    }

    function hasPendingTimelineFocusRequest(
        request: RoomTimelineFocusRequest | undefined
    ): request is RoomTimelineFocusRequest {
        return request !== undefined && request.roomId === room.id && request.sequence > lastTimelineFocusSequence;
    }

    async function focusPendingTimelineRequestOrScrollToBottom() {
        const pendingFocusRequest = get(roomTimelineFocusStore);

        if (hasPendingTimelineFocusRequest(pendingFocusRequest)) {
            await focusTimelineEvent(pendingFocusRequest);
            return;
        }

        scrollToMessageListBottom();
    }

    beforeUpdate(() => {
        if (messageListRef) {
            oldScrollHeight = messageListRef.scrollHeight;
            const scrollableDistance = messageListRef.scrollHeight - messageListRef.offsetHeight;
            autoScroll = messageListRef.scrollTop > scrollableDistance - 20;
            onScrollTop = messageListRef.scrollTop === 0;
        }
    });

    afterUpdate(() => {
        if ($initializationState === "ready") {
            room.setTimelineAsRead();
        }
        if (autoScroll) {
            scrollToMessageListBottom();
        } else if (onScrollTop) {
            const oldFirstListItem = messageListRef.querySelector<HTMLLIElement>('li[data-first-li="true"]');

            if (oldFirstListItem !== null) {
                const newScrollHeight = messageListRef.scrollHeight;
                messageListRef.scrollTop = newScrollHeight - oldScrollHeight;
            }
            setFirstListItem();
        }
    });

    function scrollToMessageListBottom() {
        if (messageListRef == undefined) return;
        messageListRef.scroll({ top: messageListRef.scrollHeight, behavior: "smooth" });
    }

    function goBackAndClearSelectedChatMessage() {
        selectedChatMessageToReply.set(null);
        if (backAction) {
            backAction();
            shouldRestoreChatStateStore.set(false);
            return;
        }
        if (room.conversationKind === "thread" && room.parentRoom) {
            selectedRoomStore.set(room.parentRoom);
        } else {
            selectedRoomStore.set(undefined);
        }
        shouldRestoreChatStateStore.set(false);

        if (room instanceof ProximityChatRoom) {
            intentionallyClosedChatDuringMeetingStore.set(true);
        }
    }

    function handleScroll() {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
            loadMorePreviousMessages();
        }, 100);
    }

    function loadMorePreviousMessages() {
        if (loadingMessagePromise || !shouldLoadMoreMessages()) return;

        loadingMessagePromise = new Promise<void>((resolve) => {
            (async () => {
                const loadMessages = async () => {
                    if (messageListRef.scrollTop === 0) {
                        shouldDisplayLoader = true;
                    }
                    await room.loadMorePreviousMessages();

                    if (shouldLoadMoreMessages()) {
                        loadMorePreviousMessages();
                    }
                };

                await loadMessages();
            })()
                .catch((error) => {
                    console.error(`Failed to load messages: ${error}`);
                    throw new Error(`Failed to load messages: ${error}`);
                })
                .finally(() => {
                    if (shouldDisplayLoader) {
                        shouldDisplayLoader = false;
                    }
                    resolve();
                });
        }).finally(() => {
            loadingMessagePromise = undefined;
        });
    }

    function shouldLoadMoreMessages() {
        const messages = Array.from(messageListRef?.querySelectorAll("li") || []);
        const messagesBeforeViewportTop = messages.find((msg) => msg.getBoundingClientRect().top >= 0);

        return (
            messagesBeforeViewportTop &&
            messages.indexOf(messagesBeforeViewportTop) < 10 &&
            messages.indexOf(messagesBeforeViewportTop) !== -1 &&
            get(room.hasPreviousMessage)
        );
    }

    function setFirstListItem() {
        const oldFirstListItem = messageListRef.querySelector<HTMLLIElement>('li[data-first-li="true"]');
        oldFirstListItem?.removeAttribute("data-first-li");

        const firstListItem = messageListRef.getElementsByTagName("li").item(0);
        firstListItem?.setAttribute("data-first-li", "true");
    }

    function isViewportNotFilled() {
        return messageListRef.scrollHeight <= messageListRef.clientHeight;
    }

    function onUpdateMessageBody(event: CustomEvent) {
        const currentTimelineItems = get(timelineItems);
        const lastTimelineItem = currentTimelineItems[currentTimelineItems.length - 1];
        const lastMessage = lastTimelineItem?.kind === "message" ? lastTimelineItem.message : undefined;
        if (
            autoScroll ||
            (event.detail != undefined &&
                lastMessage &&
                event.detail.id === lastMessage.id &&
                lastMessage.sender?.chatId === myChatID)
        ) {
            scrollToMessageListBottom();
        }
    }

    function onDropFiles(event: DragEvent) {
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            messageInputBarRef.handleFiles({
                detail: event.dataTransfer.files,
            } as CustomEvent<FileList>);
        }
    }

    async function focusTimelineEvent(request: RoomTimelineFocusRequest) {
        if (!hasPendingTimelineFocusRequest(request)) {
            return;
        }

        await tick();

        let target = Array.from(messageListRef?.querySelectorAll<HTMLLIElement>("li[data-event-id]") ?? []).find(
            (element) => element.dataset.eventId === request.eventId
        );

        if (!target) {
            const wasMadeVisible = (await room.ensureTimelineEventVisible?.(request.eventId)) ?? false;
            if (wasMadeVisible) {
                await tick();
                target = Array.from(messageListRef?.querySelectorAll<HTMLLIElement>("li[data-event-id]") ?? []).find(
                    (element) => element.dataset.eventId === request.eventId
                );
            }
        }

        if (!target) {
            return;
        }

        lastTimelineFocusSequence = request.sequence;
        target.scrollIntoView({ block: "center", behavior: "smooth" });
        target.classList.remove("highlight-message");
        // // Force the highlight animation to restart when the same poll is clicked again.
        // target.offsetWidth;
        target.classList.add("highlight-message");
    }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="flex flex-col flex-auto h-full w-full max-w-full"
    data-testid={timelineTestId}
    on:dragover|preventDefault
    on:drop|preventDefault|stopPropagation={onDropFiles}
>
    {#if room !== undefined}
        <div class="flex flex-col gap-2">
            <div
                class="p-2 flex items-center border border-solid border-x-0 border-b border-t-0 border-white/10 {shouldReserveHeaderEndSpace
                    ? 'pe-14'
                    : ''}"
            >
                {#if chatRoomsEnableInAdmin}
                    <button
                        class="back-roomlist p-3 hover:bg-white/10 rounded aspect-square w-12 h-12 !text-white shrink-0"
                        data-testid={backButtonTestId}
                        on:click={goBackAndClearSelectedChatMessage}
                    >
                        {#if direction === "rtl"}
                            <IconChevronRight font-size="20" />
                        {:else}
                            <IconChevronLeft font-size="20" />
                        {/if}
                    </button>
                {:else}
                    <div class="p-3 rounded aspect-square w-12 h-12 shrink-0" />
                {/if}
                <div class="text-sm font-bold min-w-0 grow text-center px-2 truncate" data-testid="roomName">
                    {$roomName}
                </div>

                {#if showRoomSidePanelToggle}
                    <button
                        type="button"
                        class="relative p-3 rounded aspect-square w-12 h-12 shrink-0 !text-white hover:bg-white/10 {roomSidePanelToggleIsOpen
                            ? 'bg-white/10'
                            : ''}"
                        data-testid="toggleRoomSidePanelButton"
                        title={roomSidePanelToggleIsOpen
                            ? $LL.chat.roomPanel.toggleClose()
                            : $LL.chat.roomPanel.toggleOpen()}
                        aria-pressed={roomSidePanelToggleIsOpen}
                        on:click={() => roomSidePanelStore.toggle()}
                    >
                        <IconInfoCircle font-size="20" />
                        {#if unreadThreadCount > 0}
                            <span
                                class="absolute right-2 top-2 h-2.5 w-2.5 rounded-full border border-solid border-contrast bg-success"
                                data-testid="toggleRoomSidePanelUnreadBadge"
                            />
                        {/if}
                    </button>
                {:else}
                    <div class="p-3 rounded aspect-square w-12 h-12 shrink-0" aria-hidden="true" />
                {/if}
            </div>
            {#if shouldDisplayLoader}
                <div class="flex justify-center items-center w-full pb-1 bg-transparent">
                    <IconLoader class="animate-[spin_2s_linear_infinite]" font-size={25} />
                </div>
            {/if}
        </div>
        <div
            bind:this={messageListRef}
            class="flex overflow-auto h-full justify-center items-end relative"
            on:scroll={handleScroll}
        >
            <ul
                class="list-none p-0 flex-1 flex flex-col max-w-full max-h-full pt-10 gap-1 {$timelineItems.length === 0
                    ? 'items-center justify-center pb-4'
                    : 'max-w-6xl'}"
            >
                {#if $initializationState === "loading" || $initializationState === "idle"}
                    <li class="flex flex-col items-center justify-center gap-3 text-center px-3 max-w-md">
                        <IconLoader class="animate-[spin_2s_linear_infinite]" font-size={32} />
                    </li>
                {:else if $initializationState === "error"}
                    <li class="flex flex-col items-center justify-center gap-3 text-center px-3 max-w-md">
                        <div class="text-lg font-bold text-center">{$LL.chat.connectionError()}</div>
                        <div class="text-sm opacity-50 text-center">
                            {$initializationError?.message ?? ""}
                        </div>
                        <button
                            class="px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-sm"
                            on:click={retryInitialization}
                        >
                            {$LL.chat.timeLine.retry()}
                        </button>
                    </li>
                {:else if $timelineItems.length === 0}
                    {#if room instanceof ProximityChatRoom}
                        <li class="text-center px-3 max-w-md">
                            <img draggable="false" src={getCloseImg} alt={$LL.chat.getCloserTitle()} />
                            <div class="text-lg font-bold text-center">{$LL.chat.getCloserTitle()}</div>
                            <div class="text-sm opacity-50 text-center">
                                {$LL.chat.getCloserDesc()}
                            </div>
                        </li>
                    {:else}
                        <li class="text-center px-3 max-w-md relative">
                            <IconMailBox font-size="40" />
                            <div class="text-lg font-bold text-center">{$LL.chat.noMessage()}</div>
                            <div class="text-sm opacity-50 text-center">{$LL.chat.beFirst()}</div>
                            <div class="absolute w-10 h-10 -bottom-1.5 -left-10">
                                <svg
                                    width="51"
                                    height="45"
                                    viewBox="0 0 51 45"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7.46934 42.1088C7.31199 42.9222 7.84378 43.7091 8.65713 43.8664C9.47048 44.0238 10.2574 43.492 10.4147 42.6786L7.46934 42.1088ZM48.8329 3.45148C49.6538 3.34051 50.2294 2.58502 50.1184 1.76406C50.0074 0.943102 49.2519 0.367545 48.431 0.47852L48.8329 3.45148ZM3.03603 31.9911C2.60916 31.2811 1.68756 31.0516 0.977578 31.4785C0.267597 31.9053 0.038091 32.8269 0.464962 33.5369L3.03603 31.9911ZM7.22548 41.8701L5.93995 42.643L5.94067 42.6442L7.22548 41.8701ZM10.842 42.9209L10.2291 41.5518L10.2278 41.5524L10.842 42.9209ZM21.7611 39.6758C22.5172 39.3373 22.8557 38.4499 22.5172 37.6938C22.1787 36.9377 21.2913 36.5992 20.5352 36.9377L21.7611 39.6758ZM8.94203 42.3937C10.4147 42.6786 10.4147 42.679 10.4146 42.6792C10.4146 42.6792 10.4146 42.6794 10.4146 42.6794C10.4146 42.6794 10.4146 42.6792 10.4147 42.6786C10.4149 42.6776 10.4153 42.6755 10.416 42.6723C10.4172 42.666 10.4193 42.6556 10.4222 42.641C10.4281 42.612 10.4375 42.5667 10.4504 42.5059C10.4762 42.3842 10.5163 42.2006 10.5721 41.9611C10.6837 41.4819 10.8576 40.7796 11.1039 39.9023C11.5969 38.1466 12.3779 35.6973 13.5261 32.9384C15.8321 27.3978 19.5724 20.7218 25.3326 15.8605L23.3977 13.5678C17.1251 18.8616 13.1538 26.0254 10.7564 31.7857C9.55296 34.6773 8.73434 37.2439 8.21562 39.0914C7.95608 40.0157 7.77112 40.7618 7.6503 41.2807C7.58987 41.5402 7.54545 41.743 7.51573 41.883C7.50087 41.953 7.48968 42.0074 7.48201 42.0452C7.47817 42.0642 7.47521 42.079 7.4731 42.0896C7.47205 42.095 7.47121 42.0992 7.47059 42.1024C7.47027 42.104 7.47001 42.1054 7.46981 42.1064C7.4697 42.107 7.46959 42.1076 7.46953 42.1078C7.46943 42.1084 7.46934 42.1088 8.94203 42.3937ZM25.3326 15.8605C37.1451 5.89135 46.5336 3.76229 48.8329 3.45148L48.431 0.47852C45.5724 0.86493 35.6508 3.2269 23.3977 13.5678L25.3326 15.8605ZM0.464962 33.5369L5.93995 42.643L8.51102 41.0972L3.03603 31.9911L0.464962 33.5369ZM5.94067 42.6442C7.08293 44.54 9.46921 45.1811 11.4562 44.2894L10.2278 41.5524C9.52671 41.8671 8.81489 41.6015 8.51029 41.096L5.94067 42.6442ZM11.4549 44.29L21.7611 39.6758L20.5352 36.9377L10.2291 41.5518L11.4549 44.29Z"
                                        fill="#FAF7F0"
                                    />
                                </svg>
                            </div>
                        </li>
                    {/if}
                {/if}
                {#each renderItems as item (item.key)}
                    <li class="last:pb-3" data-event-id={item.kind === "item" ? item.timelineItem.id : undefined}>
                        {#if item.kind === "separator"}
                            <div class="flex justify-center items-center pt-3 pb-1 px-6">
                                <span class="text-xs font-condensed min-w-32 text-center block opacity-75 py-1.5 px-3"
                                    >{item.label}</span
                                >
                            </div>
                        {:else if item.timelineItem.kind === "system"}
                            <MessageSystem message={item.timelineItem.message} />
                        {:else if item.timelineItem.kind === "poll"}
                            <PollCard poll={item.timelineItem.poll} />
                        {:else}
                            <Message
                                on:updateMessageBody={onUpdateMessageBody}
                                message={item.timelineItem.message}
                                showHeader={item.showHeader}
                                membersForMessageAvatars={room.membersForMessageAvatars}
                                showThreadSummary={room.conversationKind === "room"}
                            />
                        {/if}
                    </li>
                {/each}
            </ul>
        </div>

        {#if $initializationState === "ready" && $typingMembers.length > 0}
            <TypingUsers typingMembers={$typingMembers} />
        {/if}
        {#key room}
            <MessageInputBar
                disabled={$shouldRetrySendingEvents || $initializationState !== "ready"}
                {room}
                bind:this={messageInputBarRef}
            />
        {/key}
    {/if}
</div>

<style>
    :global(li.highlight-message) {
        background-color: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        animation: highlight-pulse 2s ease-out;
    }

    @keyframes highlight-pulse {
        0% {
            background-color: rgba(255, 255, 255, 0.3);
        }
        100% {
            background-color: rgba(255, 255, 255, 0.1);
        }
    }
</style>
